"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateAadAppDriver = void 0;
const tslib_1 = require("tslib");
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const axios_1 = tslib_1.__importDefault(require("axios"));
const typedi_1 = require("typedi");
const constants_1 = require("../../../common/constants");
const globalVars_1 = require("../../../common/globalVars");
const localizeUtils_1 = require("../../../common/localizeUtils");
const environmentName_1 = require("../../../core/environmentName");
const common_1 = require("../../../error/common");
const outputEnvironmentVariableUndefinedError_1 = require("../error/outputEnvironmentVariableUndefinedError");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const utils_1 = require("../util/utils");
const wrapUtil_1 = require("../util/wrapUtil");
const aadAppNameTooLongError_1 = require("./error/aadAppNameTooLongError");
const missingEnvError_1 = require("./error/missingEnvError");
const createAadAppOutput_1 = require("./interface/createAadAppOutput");
const signInAudience_1 = require("./interface/signInAudience");
const aadAppClient_1 = require("../../../client/aadAppClient");
const constants_2 = require("./utility/constants");
const teamsDevPortalClient_1 = require("../../../client/teamsDevPortalClient");
const accountUtils_1 = require("../../../common/accountUtils");
const actionName = "aadApp/create"; // DO NOT MODIFY the name
const helpLink = "https://aka.ms/teamsfx-actions/aadapp-create";
const driverConstants = {
    generateSecretErrorMessageKey: "driver.aadApp.error.generateSecretFailed",
};
let CreateAadAppDriver = class CreateAadAppDriver {
    description = (0, localizeUtils_1.getLocalizedString)(constants_2.descriptionMessageKeys.create);
    progressTitle = (0, localizeUtils_1.getLocalizedString)("driver.aadApp.progressBar.createAadAppTitle");
    async execute(args, context, outputEnvVarNames) {
        const wrapDriverContext = new wrapUtil_1.WrapDriverContext(context, actionName, actionName);
        return await this.executeInternal(args, wrapDriverContext, outputEnvVarNames);
    }
    async executeInternal(args, context, outputEnvVarNames) {
        const summaries = [];
        let outputs = new Map();
        if (!outputEnvVarNames) {
            const error = new outputEnvironmentVariableUndefinedError_1.OutputEnvironmentVariableUndefinedError(actionName);
            context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.failExecuteDriver, actionName, error.displayMessage));
            return {
                result: (0, teamsfx_api_1.err)(error),
                summaries: summaries,
            };
        }
        const aadAppState = (0, utils_1.loadStateFromEnv)(outputEnvVarNames);
        try {
            context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.startExecuteDriver, actionName));
            this.validateArgs(args);
            const tokenJson = await context.m365TokenProvider.getJsonObject({ scopes: constants_1.GraphScopes });
            const isMsftAccount = tokenJson.isOk() &&
                tokenJson.value &&
                typeof tokenJson.value.unique_name === "string" &&
                tokenJson.value.unique_name.endsWith("@microsoft.com");
            const aadAppClient = new aadAppClient_1.AadAppClient(context.m365TokenProvider, context.logProvider);
            if (!aadAppState.clientId) {
                context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.startCreateAadApp, outputEnvVarNames.get(createAadAppOutput_1.OutputKeys.clientId)));
                context.addTelemetryProperties({ [constants_2.telemetryKeys.newAadApp]: "true" });
                // Create new Microsoft Entra app if no client id exists
                const signInAudience = args.signInAudience
                    ? args.signInAudience
                    : signInAudience_1.SignInAudience.AzureADMyOrg;
                // This hidden environment variable is for internal use only.
                const serviceManagementReference = args.serviceManagementReference || process.env.TTK_DEFAULT_SERVICE_MANAGEMENT_REFERENCE;
                let aadApp;
                if (args.generateServicePrincipal) {
                    const tokenRes = await context.m365TokenProvider.getAccessToken({
                        scopes: (0, constants_1.AppStudioScopes)(),
                    });
                    if (tokenRes.isErr()) {
                        throw tokenRes.error;
                    }
                    const tdpClient = new teamsDevPortalClient_1.TeamsDevPortalClient();
                    aadApp = await tdpClient.createAADApp(tokenRes.value, args.name, signInAudience, serviceManagementReference, isMsftAccount);
                }
                else {
                    aadApp = await aadAppClient.createAadApp(args.name, signInAudience, serviceManagementReference, isMsftAccount);
                }
                aadAppState.clientId = aadApp.appId;
                aadAppState.objectId = aadApp.id;
                globalVars_1.AadSet.add(aadApp.appId);
                await this.setAadEndpointInfo(context.m365TokenProvider, aadAppState);
                outputs = (0, utils_1.mapStateToEnv)(aadAppState, outputEnvVarNames, [createAadAppOutput_1.OutputKeys.clientSecret]);
                let summary = (0, localizeUtils_1.getLocalizedString)(args.generateServicePrincipal
                    ? constants_2.logMessageKeys.successCreateAadAppandServicePrincipal
                    : constants_2.logMessageKeys.successCreateAadApp, aadApp.id);
                if (isMsftAccount) {
                    summary += (0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.deleteAadAfterDebugging);
                }
                context.logProvider?.info(summary);
                summaries.push(summary);
            }
            else {
                context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.skipCreateAadApp, outputEnvVarNames.get(createAadAppOutput_1.OutputKeys.clientId)));
                context.addTelemetryProperties({ [constants_2.telemetryKeys.newAadApp]: "false" });
            }
            if (args.generateClientSecret) {
                if (!aadAppState.clientSecret) {
                    context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.startGenerateClientSecret, outputEnvVarNames.get(createAadAppOutput_1.OutputKeys.clientSecret)));
                    // Create new client secret if no client secret exists
                    if (!aadAppState.objectId) {
                        throw new missingEnvError_1.MissingEnvUserError(actionName, outputEnvVarNames.get(createAadAppOutput_1.OutputKeys.objectId), helpLink, driverConstants.generateSecretErrorMessageKey);
                    }
                    const clientSecretExpireDays = args.clientSecretExpireDays ?? 180; // Recommended lifetime from Azure Portal
                    const clientSecretDescription = args.clientSecretDescription ?? "default";
                    aadAppState.clientSecret = await aadAppClient.generateClientSecret(aadAppState.objectId, clientSecretExpireDays, clientSecretDescription, isMsftAccount);
                    outputs.set(outputEnvVarNames.get(createAadAppOutput_1.OutputKeys.clientSecret), aadAppState.clientSecret);
                    const summary = (0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.successGenerateClientSecret, aadAppState.objectId);
                    context.logProvider?.info(summary);
                    summaries.push(summary);
                }
                else {
                    context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.skipCreateAadApp, outputEnvVarNames.get(createAadAppOutput_1.OutputKeys.clientSecret)));
                }
            }
            context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.successExecuteDriver, actionName));
            return {
                result: (0, teamsfx_api_1.ok)(outputs),
                summaries: summaries,
            };
        }
        catch (error) {
            if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
                context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.failExecuteDriver, actionName, error.displayMessage));
                return {
                    result: (0, teamsfx_api_1.err)(error),
                    summaries: summaries,
                };
            }
            if (axios_1.default.isAxiosError(error)) {
                const message = JSON.stringify(error.response.data);
                context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.failExecuteDriver, actionName, message));
                if (error.response.status >= 400 && error.response.status < 500) {
                    // When user don't have permission to create AAD app, we will ask for AAD app id and secret
                    if (error.response.status === 403 &&
                        message.includes(constants_2.constants.insufficientPermissionErrorMessage) &&
                        process.env.TEAMSFX_ENV == environmentName_1.environmentNameManager.getLocalEnvName()) {
                        context.addTelemetryProperties({
                            [constants_2.telemetryKeys.insufficientPermissionAadApp]: "true",
                        });
                        const res = await this.askForAADAppIdAndSecret(context, aadAppState, outputEnvVarNames);
                        if (res.isOk()) {
                            await this.setAadEndpointInfo(context.m365TokenProvider, aadAppState);
                            const outputs = (0, utils_1.mapStateToEnv)(aadAppState, outputEnvVarNames);
                            context.addTelemetryProperties({ [constants_2.telemetryKeys.userInputAadApp]: "true" });
                            return {
                                result: (0, teamsfx_api_1.ok)(outputs),
                                summaries: summaries,
                            };
                        }
                        else {
                            context.addTelemetryProperties({ [constants_2.telemetryKeys.userInputAadApp]: "false" });
                            return {
                                result: (0, teamsfx_api_1.err)(res.error),
                                summaries: summaries,
                            };
                        }
                    }
                    return {
                        result: (0, teamsfx_api_1.err)(new common_1.HttpClientError(error, actionName, message, helpLink)),
                        summaries: summaries,
                    };
                }
                else {
                    return {
                        result: (0, teamsfx_api_1.err)(new common_1.HttpServerError(error, actionName, message)),
                        summaries: summaries,
                    };
                }
            }
            const message = JSON.stringify(error);
            context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.failExecuteDriver, actionName, message));
            return {
                result: (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, actionName)),
                summaries: summaries,
            };
        }
    }
    /**
     * Pop up a dialog to ask for AAD app id and secret
     * @param context
     * @param aadAppState
     * @param outputEnvVarNames
     * @returns
     */
    async askForAADAppIdAndSecret(context, aadAppState, outputEnvVarNames) {
        const res = await context.ui.showMessage("error", (0, localizeUtils_1.getLocalizedString)(constants_2.logMessageKeys.insufficientPermission), true, "Proceed");
        if (res.isOk() && res.value == "Proceed") {
            const aadAppId = await context.ui.inputText({
                name: "aadAppId",
                title: (0, localizeUtils_1.getLocalizedString)(constants_2.questionKeys.aadAppIdTitle),
                validation: (input) => {
                    if (input.length < 1) {
                        return (0, localizeUtils_1.getLocalizedString)(constants_2.questionKeys.addAppIdValidation);
                    }
                },
            });
            if (aadAppId.isErr()) {
                return (0, teamsfx_api_1.err)(new common_1.UserCancelError(actionName));
            }
            const aadAppSecret = await context.ui.inputText({
                name: "aadAppSecret",
                title: (0, localizeUtils_1.getLocalizedString)(constants_2.questionKeys.aadAppSecretTitle),
                password: true,
                validation: (input) => {
                    if (input.length < 1) {
                        return (0, localizeUtils_1.getLocalizedString)(constants_2.questionKeys.aadAppSecretValidation);
                    }
                },
            });
            if (aadAppSecret.isErr()) {
                return (0, teamsfx_api_1.err)(new common_1.UserCancelError(actionName));
            }
            const aadAppObjectId = await context.ui.inputText({
                name: "aadAppObjectId",
                title: (0, localizeUtils_1.getLocalizedString)(constants_2.questionKeys.aadAppObjectIdTitle),
                validation: (input) => {
                    if (input.length < 1) {
                        return (0, localizeUtils_1.getLocalizedString)(constants_2.questionKeys.aadAppObjectIdValidation);
                    }
                },
            });
            if (aadAppObjectId.isErr()) {
                return (0, teamsfx_api_1.err)(new common_1.UserCancelError(actionName));
            }
            aadAppState.clientId = aadAppId.value.result;
            globalVars_1.AadSet.add(aadAppState.clientId);
            aadAppState.clientSecret = aadAppSecret.value.result;
            aadAppState.objectId = aadAppObjectId.value.result;
            const outputs = (0, utils_1.mapStateToEnv)(aadAppState, outputEnvVarNames);
            return (0, teamsfx_api_1.ok)(outputs);
        }
        else {
            return (0, teamsfx_api_1.err)(new common_1.UserCancelError(actionName));
        }
    }
    validateArgs(args) {
        const invalidParameters = [];
        if (typeof args.name !== "string" || !args.name) {
            invalidParameters.push("name");
        }
        if (args.generateClientSecret === undefined || typeof args.generateClientSecret !== "boolean") {
            invalidParameters.push("generateClientSecret");
        }
        // Throw error if unexpected signInAudience
        if (args.signInAudience &&
            (typeof args.signInAudience !== "string" ||
                !Object.values(signInAudience_1.SignInAudience).includes(args.signInAudience))) {
            invalidParameters.push("signInAudience");
        }
        if (invalidParameters.length > 0) {
            throw new common_1.InvalidActionInputError(actionName, invalidParameters, helpLink);
        }
        if (args.name.length > 120) {
            throw new aadAppNameTooLongError_1.AadAppNameTooLongError(actionName);
        }
    }
    // logic from
    // src\component\resource\aadApp\utils\tokenProvider.ts
    // src\component\resource\aadApp\utils\configs.ts
    async setAadEndpointInfo(tokenProvider, state) {
        const tokenObjectResponse = await tokenProvider.getJsonObject({ scopes: constants_1.GraphScopes });
        if (tokenObjectResponse.isErr()) {
            throw tokenObjectResponse.error;
        }
        const tenantId = tokenObjectResponse.value.tid; // The tid claim is AAD tenant id
        state.tenantId = tenantId;
        state.authorityHost = (0, accountUtils_1.getEntraEndpoint)();
        state.authority = (0, accountUtils_1.getTenantedAuthorityUrl)(tenantId);
    }
};
exports.CreateAadAppDriver = CreateAadAppDriver;
tslib_1.__decorate([
    (0, lib_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext,
        Map]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateAadAppDriver.prototype, "executeInternal", null);
exports.CreateAadAppDriver = CreateAadAppDriver = tslib_1.__decorate([
    (0, typedi_1.Service)(actionName) // DO NOT MODIFY the service name
], CreateAadAppDriver);
//# sourceMappingURL=create.js.map